import React, { useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import classnames from 'classnames';

import Tooltip, { TooltipProps } from './Tooltip';
import Typography, { TypographyProps } from './Typography';

const useStyles = (theme: Theme) =>
  createStyles({
    container: {
      cursor: 'pointer',
      padding: `${theme.spacing.unit / 2}px ${theme.spacing.unit}px`,
      border: `2px solid ${lighten(theme.palette.primary.main, 0.8)}`,
      borderRadius: theme.spacing.unit / 2,
      backgroundColor: theme.custom.palette.colorVeryLightGray,
      textAlign: 'center',
      minWidth: 205,
      transition: 'background-color 0.5s ease',
      '&$compacted': {
        minWidth: 80,
      },
      '&:hover': {
        backgroundColor: lighten(theme.palette.primary.main, 0.8),
      },
    },
    compacted: {},
  });

const messages = defineMessages({
  copyId: {
    id: 'components.v2.CopyIdToClipboard.copyId',
    defaultMessage: 'Copy ID',
  },
  copied: {
    id: 'components.v2.CopyIdToClipboard.copied',
    defaultMessage: 'Copied!',
  },
});

export interface CopyIdToClipboardProps extends WithStyles<typeof useStyles> {
  id: string;
  compacted?: boolean;
  toolTipProps?: TooltipProps;
  typographyProps?: TypographyProps;
}

/**
 * Component for displaying and copying resource id
 * When `compact` only the last 7 id's digit are shown.
 * @component
 * @example
 * const { _id } = lumabotFlowData;
 * return (
 *   <CopyIdToClipboard id={_id} compacted />
 * )
 */
const CopyIdToClipboard: React.FC<CopyIdToClipboardProps> = ({
  id,
  compacted,
  classes,
  toolTipProps,
  typographyProps,
  ...otherProps
}) => {
  const { formatMessage } = useIntl();
  const [isCopied, setCopied] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (isCopied) timer = setTimeout(() => setCopied(false), 2000);

    return () => {
      clearTimeout(timer);
    };
  }, [isCopied]);

  return (
    <CopyToClipboard text={id}>
      <Tooltip
        data-lh-id={`${id}-copyToClipboard-btn`}
        title={formatMessage(messages.copyId)}
        placement="top"
        onClick={() => setCopied(true)}
        {...toolTipProps}
      >
        <div className={classnames(classes.container, { [classes.compacted]: compacted })} {...otherProps}>
          <Typography variant="bW400F12" monospaced {...typographyProps}>
            {isCopied ? formatMessage(messages.copied) : `#${compacted ? id?.slice(-7) : id}`}
          </Typography>
        </div>
      </Tooltip>
    </CopyToClipboard>
  );
};

export default withStyles(useStyles)(CopyIdToClipboard);
